/**
 * Native session-sync wiring for the shared terminal.
 *
 * Binds the protocol-pure sync engine (`./sync`) to the terminal's own
 * `ChatHistoryStore`, so cross-device chat history is a BUILT-IN feature of
 * `<Terminal>` — not a per-host adapter. Auth + storage are already the
 * terminal's job (core owns both), so there is nothing host-specific to
 * inject: every consumer (Paradex, Paradigm, standalone) gets sync by
 * pairing a gateway that serves `/sync/v1/*`.
 *
 * The engine degrades gracefully: every request returns null on failure, so
 * a gateway without `/sync/v1/*` (or a cross-origin gateway without CORS)
 * simply stays local-only — no errors, no config.
 *
 * Scope: sessions index (+ folders + tombstones), per-session transcripts, and
 * the user's explicit preferences (the `ui-state` doc). Incidental view state
 * — which folders are collapsed, which session was last open — stays
 * deliberately out of scope: it's per-device by nature, and `HistoryUiState`
 * keeps it local. What syncs is what the user chose on purpose.
 */

import {
  clearLiveMessageFlags,
  isPersistedChatMessage,
  sanitizeMessageAttachments,
  sanitizeMessageThinking,
  sanitizeMessageToolCalls,
  stripThinkingForSync,
} from './chat-history-storage';
import type {
  ChatHistoryStore,
  DeletionTombstone,
} from './chat-history-storage';
import {
  EMPTY_PREFERENCES_DOC,
  mergePreferences,
  normalizePreferencesDoc,
  toSyncedPreferencesDoc,
  preferencesDocsEqual,
  hasClampedStamp,
} from './preferences';
import type { PreferencesDoc, PreferencesStore } from './preferences';
import {
  emptyIndexDoc,
  mergeIndex,
  mergeMessages,
  syncBaseUrlFromAgentUrl,
  TerminalSyncClient,
} from './sync';
import type {
  SyncDocRef,
  SyncIndexDoc,
  SyncMessagesDoc,
  SyncSnapshot,
  TerminalSyncStatus,
} from './sync';
import type { ChatMessage, ChatSession, CustomFolder } from './types';

export type TerminalIndexDoc = SyncIndexDoc<ChatSession, CustomFolder>;
export type TerminalMessagesDoc = SyncMessagesDoc<ChatMessage>;
export type TerminalSync = TerminalSyncClient<
  ChatMessage,
  ChatSession,
  CustomFolder
>;

/** Index doc for upload: persisted sessions (temporary chats never sync). */
export function buildIndexDoc(
  store: ChatHistoryStore,
  accountId: string,
  sessions: readonly ChatSession[],
  folders: readonly CustomFolder[],
): TerminalIndexDoc {
  return {
    sessions: sessions.filter((s) => s.isTemporary !== true),
    folders,
    deleted: store.loadTombstones(accountId),
  };
}

function asIndexDoc(value: unknown): TerminalIndexDoc {
  const v = (value ?? {}) as Partial<TerminalIndexDoc>;
  return {
    sessions: Array.isArray(v.sessions) ? v.sessions : [],
    folders: Array.isArray(v.folders) ? v.folders : [],
    deleted: Array.isArray(v.deleted) ? v.deleted : [],
  };
}

/**
 * Whether two transcripts hold the same set of message ids. Used as the
 * hydration change signal: what matters is whether the stored blob is a
 * different SET from what was on disk, which neither length nor order answers
 * once a trim can add and drop in the same pass.
 */
function sameMessageIds(
  a: readonly ChatMessage[],
  b: readonly ChatMessage[],
): boolean {
  if (a.length !== b.length) return false;
  const ids = new Set(b.map((m) => m.id));
  return a.every((m) => ids.has(m.id));
}

/**
 * Coerce a remote transcript document on the way in. Exported so the boundary
 * can be asserted directly: a store double that re-does this work would hide
 * a regression here entirely.
 */
export function asMessagesDoc(value: unknown): TerminalMessagesDoc {
  const v = (value ?? {}) as Partial<TerminalMessagesDoc>;
  if (!Array.isArray(v.messages)) return { messages: [] };
  // Remote docs are otherwise adopted without per-message validation, so
  // coerce at this boundary, same as parseMessages does for localStorage:
  //
  //  - attachment previewUrls end up in an <img src>;
  //  - a reasoning trace is rendered as a React child, where a non-string
  //    throws the whole transcript rather than degrading;
  //  - the live flags are meaningless outside the run that set them. A device
  //    that died mid-run uploads `isStreaming`/`isThinking` still true, and
  //    every terminator here is scoped to a `runId` that will never match
  //    again — so an adopted message would show stuck dots or a block
  //    shimmering forever, and the persist effect would re-upload it and
  //    re-infect every other device.
  //
  // Entries are validated with the SAME predicate `parseMessages` uses, which
  // is what "parity with localStorage" above actually requires: rejecting only
  // non-objects still admits `{ id, role, timestamp }` with no `content`, and
  // the next reader of `m.content.length` throws. On the 412 path that breaks
  // the sync client's `resolveConflict` contract ("must never throw") —
  // `drain`'s defensive catch leaves the op pending, so the session re-attempts
  // the same doomed merge every 30s forever.
  return {
    messages: v.messages
      .filter((m): m is ChatMessage => isPersistedChatMessage(m))
      .map((m) =>
        clearLiveMessageFlags(
          sanitizeMessageToolCalls(
            sanitizeMessageThinking(sanitizeMessageAttachments(m)),
          ),
        ),
      ),
  };
}

/**
 * The `ui-state` sync doc. The web-api treats every sync document body as
 * opaque host-defined JSON, so this shape is ours to define, and it is wrapped
 * in a named field rather than stored bare so a later addition (another
 * preference group, per-device state) has somewhere to go.
 *
 * NOT yet additive in practice, though, and this is the correction to a claim
 * this comment used to make. `asUiStateDoc` rebuilds the document as exactly
 * `{ preferences }` and `normalizePreferencesDoc` iterates only
 * `PREFERENCE_KEYS`, so anything a newer build writes — a new preference
 * field, a sibling ui-state key — is DROPPED on read and then re-uploaded
 * without it. That costs nothing today, because no such field exists. It
 * becomes a mixed-version fleet deleting each other's settings on the first
 * rollout that adds one.
 *
 * So carrying unknown keys through is work owed BEFORE the next field ships,
 * not after — afterwards it is a v1→v2 migration instead of a shape change.
 * It reaches four places: here, `normalizePreferencesDoc` (carry them),
 * `mergePreferences` (LWW over the union rather than the known keys), and
 * `preferencesDocsEqual` (or the upload guard stops seeing changes to carried
 * keys and the document never converges) — plus a size bound, since a carried
 * key is unvalidated by definition. `hasClampedStamp` enumerates
 * `PREFERENCE_KEYS` too and is a fifth site to revisit then.
 *
 * `version` is written but never read, which is the other half of the same
 * gap: there is no reader to branch on if the shape ever does change.
 *
 * Until that lands, a new preference stays out of this document:
 * `DEVICE_LOCAL_PREFERENCE_KEYS` names them and `toSyncedPreferencesDoc`
 * strips them at both ends of the boundary.
 */
export interface TerminalUiStateDoc {
  readonly preferences: PreferencesDoc;
}

function asUiStateDoc(value: unknown): TerminalUiStateDoc {
  const v = (value ?? {}) as Partial<TerminalUiStateDoc>;
  // Stripped on READ as well as on write: a device-local field must not enter
  // a merge from the remote side, and the upload guard compares against this
  // doc — an unstripped one would differ from every stripped upload and PUT on
  // every launch.
  return {
    preferences: toSyncedPreferencesDoc(normalizePreferencesDoc(v.preferences)),
  };
}

function resolveTerminalConflict(
  ref: SyncDocRef,
  local: unknown,
  remote: unknown,
): unknown {
  if (ref.kind === 'index') {
    return mergeIndex(asIndexDoc(local), asIndexDoc(remote));
  }
  if (ref.kind === 'session') {
    // Wire shape, because this document goes back on the WIRE. The 412 retry
    // is the one upload that doesn't come from the persist effect, so without
    // this it PUTs the raw union — over the byte budget the doc cap exists to
    // respect, and carrying reasoning traces that never leave a device (see
    // `stripThinkingForSync`). A 413 on the retry is retryable, so an
    // oversized merge would be recomputed on every attempt instead of
    // converging.
    return {
      messages: stripThinkingForSync(
        mergeMessages(
          asMessagesDoc(local).messages,
          asMessagesDoc(remote).messages,
        ),
      ),
    };
  }
  if (ref.kind === 'ui-state') {
    return {
      preferences: mergePreferences(
        asUiStateDoc(local).preferences,
        asUiStateDoc(remote).preferences,
      ),
    } satisfies TerminalUiStateDoc;
  }
  return local;
}

function persistMergedDoc(
  store: ChatHistoryStore,
  prefsStore: PreferencesStore | null,
  accountId: string,
  ref: SyncDocRef,
  merged: unknown,
  onPreferencesMerged: ((doc: PreferencesDoc) => void) | null,
): void {
  if (ref.kind === 'index') {
    const doc = asIndexDoc(merged);
    store.saveSessions(accountId, doc.sessions);
    store.saveCustomFolders(accountId, doc.folders);
    store.saveTombstones(
      accountId,
      doc.deleted as readonly DeletionTombstone[],
    );
    return;
  }
  if (ref.kind === 'session') {
    // MERGE into the on-disk blob, don't replace it. `merged` is the wire doc
    // the conflict retry is about to PUT, and the wire carries no reasoning
    // traces — writing it straight to disk would strip every trace this
    // device holds for the chat. `mergeMessages` is local-wins by id, so the
    // local copies (with their traces) stand and only the other device's new
    // messages come in.
    store.saveSessionMessages(
      accountId,
      ref.sessionId,
      mergeMessages(
        store.loadSessionMessages(accountId, ref.sessionId),
        asMessagesDoc(merged).messages,
      ),
    );
    return;
  }
  if (ref.kind === 'ui-state') {
    const preferences = asUiStateDoc(merged).preferences;
    // MERGE into what is on disk now, don't replace it — the same rule (and
    // reason) as `adoptDoc` on the React side: `merged` was resolved from the
    // doc as it stood when the losing PUT was snapshotted, and the user may
    // have edited since. The edit's field carries the newer stamp, so it
    // survives the merge; replacing rolled it back on disk, to be resurrected
    // only if React state happened to write again.
    if (prefsStore != null) {
      prefsStore.save(
        accountId,
        mergePreferences(prefsStore.load(accountId), preferences),
      );
    }
    // Persisting alone is not enough: the running app renders (and seeds new
    // sessions from) React state, not localStorage. A 412 fires this on every
    // losing cross-device write, so without the adopt the merge the user was
    // promised would exist only on disk — and the next `setPreference`, built
    // from the stale doc, would keep re-uploading the value that just lost.
    // Notified even with no `prefsStore`: live state is the point, the write
    // is the optional half.
    onPreferencesMerged?.(preferences);
  }
}

/**
 * Create a sync client for this account, or null when there is no usable
 * gateway URL yet. The caller owns the lifecycle (`dispose()` on account
 * switch / unmount).
 */
export function createSessionSync(params: {
  readonly store: ChatHistoryStore;
  /**
   * Preferences store the merged `ui-state` doc is written back to. Omit and
   * preference sync is simply off (the doc still merges in memory, but nothing
   * persists it) — same graceful-degradation contract as the rest of sync.
   */
  readonly prefsStore?: PreferencesStore;
  /**
   * Called with the merged `ui-state` preferences whenever a conflicting write
   * is resolved (the 412 path), so the caller can move them into live state.
   * The hydration path reports its own merge via `RemoteHydrationResult`; this
   * is the same contract for conflicts, which can happen at any time.
   */
  readonly onPreferencesMerged?: (doc: PreferencesDoc) => void;
  /**
   * Sync health for a UI indicator: what is queued, the last failure, and the
   * session documents the client has given up on. Fires on every queue and
   * drain, so consumers should compare before re-rendering.
   */
  readonly onStatusChange?: (status: TerminalSyncStatus) => void;
  readonly accountId: string;
  readonly agentUrl: string | null;
  /**
   * Explicit sync base URL (no trailing slash) — e.g. a backend forward that
   * injects the gateway credential server-side. When omitted, the base is
   * derived from the gateway ws URL.
   */
  readonly baseUrl?: string;
  /**
   * Auth headers for every sync request, or null when no credential is
   * available (treated as transient — the client keeps retrying).
   */
  readonly getAuthHeaders: () => Readonly<Record<string, string>> | null;
}): TerminalSync | null {
  const baseUrl = params.baseUrl ?? syncBaseUrlFromAgentUrl(params.agentUrl);
  if (baseUrl == null) return null;
  return new TerminalSyncClient<ChatMessage, ChatSession, CustomFolder>({
    baseUrl,
    getAuthHeaders: params.getAuthHeaders,
    resolveConflict: resolveTerminalConflict,
    onStatusChange: params.onStatusChange,
    onMerged: (ref, merged) =>
      persistMergedDoc(
        params.store,
        params.prefsStore ?? null,
        params.accountId,
        ref,
        merged,
        params.onPreferencesMerged ?? null,
      ),
  });
}

/** One transcript pulled lazily (e.g. opening a session created elsewhere). */
export async function fetchAndMergeSessionFromRemote(
  store: ChatHistoryStore,
  accountId: string,
  client: TerminalSync,
  sessionId: string,
): Promise<readonly ChatMessage[] | null> {
  const envelope = await client.fetchSession(sessionId);
  if (envelope == null) return null;
  // Honor the session's `clearedAt` tombstone, like `hydrateFromRemote`: a
  // cleared chat's old transcript intentionally stays on the gateway (the
  // empty-PUT guard never uploads `[]`), and this pull fires exactly when the
  // local blob is empty — the post-clear state — so an unfiltered merge would
  // resurrect the cleared messages (e.g. clear → switch away → switch back).
  const clearedAt =
    store.loadSessions(accountId).find((s) => s.sessionId === sessionId)
      ?.clearedAt ?? 0;
  const remote = asMessagesDoc(envelope.doc).messages.filter(
    (m) => m.timestamp > clearedAt,
  );
  if (remote.length === 0) return null;
  const merged = mergeMessages(
    store
      .loadSessionMessages(accountId, sessionId)
      .filter((m) => m.timestamp > clearedAt),
    remote,
  );
  // Return what was WRITTEN, not the pre-trim merge: the caller adopts this
  // straight into React state, and the untrimmed array still carries anything
  // the trim removes (live flags from a remote doc, over-budget traces). The
  // two must not disagree, or the screen shows what the disk does not.
  return store.saveSessionMessages(accountId, sessionId, merged);
}

export interface RemoteHydrationResult {
  /** Merged sessions index (already persisted to the store). */
  readonly sessions: readonly ChatSession[];
  /** Merged custom folders (already persisted). */
  readonly folders: readonly CustomFolder[];
  /** Session ids whose transcripts changed locally during the merge. */
  readonly updatedSessionIds: ReadonlySet<string>;
  /**
   * Merged preferences (already persisted when a `prefsStore` was passed to
   * `createSessionSync`). The caller adopts these into live state — a theme or
   * default-model change made on another device applies on this load.
   */
  readonly preferences: PreferencesDoc;
}

/**
 * Settings convergence, shared by the full hydration and the
 * preferences-only one.
 *
 * Field-level LWW, so this is a true merge rather than a pick: a default
 * model set here and a theme set on another device both survive. Only queues
 * an upload when the merge actually moved something — otherwise every load
 * would PUT an identical doc and burn a rev.
 */
function convergePreferences(
  accountId: string,
  client: TerminalSync,
  snapshot: SyncSnapshot<ChatMessage, ChatSession, CustomFolder>,
  prefsStore?: PreferencesStore,
  livePrefs?: PreferencesDoc,
): PreferencesDoc {
  // The local side is the union of everything this device knows: the disk
  // copy (which another tab may have advanced) merged with live state (which
  // holds an edit a failed `store.save` never persisted) — see `livePrefs`.
  const diskPrefs = prefsStore?.load(accountId) ?? EMPTY_PREFERENCES_DOC;
  const localPrefs =
    livePrefs == null ? diskPrefs : mergePreferences(diskPrefs, livePrefs);
  const rawRemotePrefs = (
    (snapshot.uiState?.doc ?? {}) as Partial<TerminalUiStateDoc>
  ).preferences;
  const remotePrefs = asUiStateDoc(snapshot.uiState?.doc).preferences;
  const mergedPrefs = mergePreferences(localPrefs, remotePrefs);
  if (prefsStore != null && !preferencesDocsEqual(mergedPrefs, localPrefs)) {
    prefsStore.save(accountId, mergedPrefs);
  }
  // What goes back up, and what the upload decision is made against. Disk
  // keeps the full doc; the wire never sees a device-local field.
  const uploadPrefs = toSyncedPreferencesDoc(mergedPrefs);
  // Compared against the NORMALIZED remote, so a doc we merely tidied on read
  // isn't PUT straight back. The exception is a clamped stamp: normalizing
  // hides the repair from this comparison, but the gateway is still holding a
  // far-future value that will re-pin its field on the next read — and keep
  // doing so for as long as this device cannot get a write out.
  if (
    !preferencesDocsEqual(uploadPrefs, remotePrefs) ||
    hasClampedStamp(rawRemotePrefs)
  ) {
    client.queueUiState({
      preferences: uploadPrefs,
    } satisfies TerminalUiStateDoc);
  }
  return mergedPrefs;
}

/**
 * Merge the server snapshot into local storage and queue uploads for anything
 * that exists only locally (including the one-shot migration of pre-sync
 * history). Returns null when the gateway is unreachable — the caller
 * continues local-only and the UI never blocks.
 *
 * Data-safety invariants: merges are unions (neither side dropped); tombstones
 * remove transcripts on both sides; local-only history is uploaded, not
 * replaced.
 *
 * `preferencesOnly` skips the whole session half — no index merge, no
 * transcript merge, no session or index upload — and converges settings
 * alone. That is what the terminal now asks for: the GATEWAY owns the chat
 * list and every transcript (`sessions.*`, `chat.history`), so touching them
 * from here would have a stale snapshot overwrite the store the gateway read
 * just filled, and would keep uploading documents nothing reads.
 */
export async function hydrateFromRemote(
  store: ChatHistoryStore,
  accountId: string,
  client: TerminalSync,
  prefsStore?: PreferencesStore,
  /**
   * The preferences doc as LIVE STATE holds it, when the caller has one.
   * `store.save` swallows quota and private-mode failures, so an edit can
   * exist only in memory — and a hydration that read only the disk copy
   * built its converging upload without that edit, coalescing over the
   * edit's own queued PUT (the sync client keeps one latest payload per
   * doc). Merged with the disk copy rather than replacing it, so an edit
   * another tab managed to persist is carried too.
   */
  livePrefs?: PreferencesDoc,
  options?: { readonly preferencesOnly?: boolean },
): Promise<RemoteHydrationResult | null> {
  const snapshot = await client.fetchSnapshot();
  if (snapshot == null) return null;
  // NOTE the app only ever takes this branch (`AgentProvider` passes
  // `preferencesOnly` unconditionally), which makes everything below it
  // unreachable from the product: the session half of `persistMergedDoc` and
  // of `resolveTerminalConflict` are reached only through `queueSession`,
  // whose only caller is the non-`preferencesOnly` path. Their tests still
  // pass and should not be read as coverage of shipping behaviour — they
  // cover a path kept for the hosts that have not moved to a gateway-owned
  // chat list, and it is dead the moment the last one does.
  if (options?.preferencesOnly === true) {
    return {
      sessions: [],
      folders: [],
      updatedSessionIds: new Set<string>(),
      preferences: convergePreferences(
        accountId,
        client,
        snapshot,
        prefsStore,
        livePrefs,
      ),
    };
  }

  const localIndex = buildIndexDoc(
    store,
    accountId,
    store.loadSessions(accountId),
    store.loadCustomFolders(accountId),
  );
  const remoteIndex =
    snapshot.index == null
      ? emptyIndexDoc<ChatSession, CustomFolder>()
      : asIndexDoc(snapshot.index.doc);
  const merged = mergeIndex(localIndex, remoteIndex);
  store.saveSessions(accountId, merged.sessions);
  store.saveCustomFolders(accountId, merged.folders);
  store.saveTombstones(
    accountId,
    merged.deleted as readonly DeletionTombstone[],
  );

  const liveIds = new Set(merged.sessions.map((s) => s.sessionId));
  const remoteIds = new Set(snapshot.sessionList.map((item) => item.id));
  const clearedAtById = new Map(
    merged.sessions.map((s) => [s.sessionId, s.clearedAt ?? 0] as const),
  );
  const updatedSessionIds = new Set<string>();

  // Merge snapshot transcripts into local blobs. Drop messages older than the
  // session's `clearedAt` (the user explicitly wiped them on some device).
  for (const [sessionId, envelope] of Object.entries(snapshot.sessions)) {
    if (!liveIds.has(sessionId)) continue; // tombstoned — skip
    const clearedAt = clearedAtById.get(sessionId) ?? 0;
    const raw = store.loadSessionMessages(accountId, sessionId);
    const local = raw.filter((m) => m.timestamp > clearedAt);
    const remote = asMessagesDoc(envelope.doc).messages.filter(
      (m) => m.timestamp > clearedAt,
    );
    const mergedMessages = mergeMessages(local, remote);
    // Flag the session whenever the blob that gets WRITTEN differs from what
    // was on disk before — either the merge pulled in remote messages, or a
    // remote `clearedAt` dropped local ones, or the trim dropped something.
    //
    // Compared as ID SETS, not lengths. Lengths were exact only while the
    // written blob was a superset of what was on disk; the trim breaks that in
    // both directions, and each direction is its own bug:
    //
    //  - comparing the PRE-trim merge flags every round forever, because the
    //    trim's drops are re-applied on each pass — and `applyRemoteHydration`
    //    then replaces the open transcript with the disk snapshot on every
    //    reconnect, clobbering live streaming state mid-run;
    //  - comparing post-trim LENGTHS silently cancels, because a drop and an
    //    addition net to zero. A session that genuinely gained messages goes
    //    unflagged, the provider keeps its stale transcript, and the persist
    //    effect re-uploads that stale array at the snapshot's rev — DELETING
    //    the new messages from the gateway document.
    //
    // Set comparison is exact in both directions and still converges: a round
    // that changes nothing writes the same ids it read.
    const stored = store.saveSessionMessages(
      accountId,
      sessionId,
      mergedMessages,
    );
    // Ids alone cannot see one more case: `mergeMessages` SWAPS a truncated
    // local copy for the gateway's whole one — same id, same count, different
    // content. Missing it left the open chat on its stale transcript and the
    // persist effect wrote the half-answer straight back over the complete
    // copy the merge had just chosen.
    //
    // Keyed by id, never positional: `mergedMessages` is sorted by
    // (timestamp, id) while `local` is in insertion order, so a positional
    // diff reports a change for pure REORDERING — which happens on every
    // agent-spec render, where the preamble and the spec share a millisecond
    // and `agent-spec-…` sorts first.
    const mergedById = new Map(mergedMessages.map((m) => [m.id, m]));
    const swapped = local.some((m) => mergedById.get(m.id) !== m);
    if (swapped || !sameMessageIds(stored, raw)) {
      updatedSessionIds.add(sessionId);
    }
  }

  // A remote clear must also reach sessions whose transcript was NOT bundled
  // in the snapshot (only the most-recent transcripts are): their merged
  // index entry can still carry a fresher `clearedAt`, and without this pass
  // the stale local blob would survive — and be re-uploaded by the local-only
  // migration below or the provider's persist effect.
  const bundledIds = new Set(Object.keys(snapshot.sessions));
  for (const s of merged.sessions) {
    const clearedAt = s.clearedAt ?? 0;
    if (clearedAt === 0 || bundledIds.has(s.sessionId)) continue;
    const raw = store.loadSessionMessages(accountId, s.sessionId);
    const filtered = raw.filter((m) => m.timestamp > clearedAt);
    if (filtered.length !== raw.length) {
      store.saveSessionMessages(accountId, s.sessionId, filtered);
      updatedSessionIds.add(s.sessionId);
    }
  }

  // Apply tombstones locally and propagate session deletions to the server
  // (skip the DELETE when the server doesn't have the doc).
  for (const tombstone of merged.deleted) {
    if (tombstone.kind !== 'session') continue;
    if (liveIds.has(tombstone.id)) continue; // resurrected by later activity
    store.deleteSessionMessages(accountId, tombstone.id);
    if (remoteIds.has(tombstone.id)) {
      client.queueDeleteSession(tombstone.id);
    }
  }

  // Upload local-only transcripts (also the first-run migration of pre-sync
  // history against an empty server).
  for (const session of merged.sessions) {
    if (remoteIds.has(session.sessionId)) continue;
    // Wire shape on the way out rather than trusting the blob: it is only
    // already trimmed if it was written by the CURRENT trim, which is false
    // right after an upgrade that tightened one, and after any write that hit
    // the quota and left an older blob in place — and the blob keeps the
    // reasoning traces the wire never carries.
    const messages = stripThinkingForSync(
      store.loadSessionMessages(accountId, session.sessionId),
    );
    if (messages.length === 0) continue;
    client.queueSession(session.sessionId, { messages });
  }

  // Converge the index.
  client.queueIndex(
    buildIndexDoc(store, accountId, merged.sessions, merged.folders),
  );

  const mergedPrefs = convergePreferences(
    accountId,
    client,
    snapshot,
    prefsStore,
    livePrefs,
  );

  return {
    sessions: merged.sessions,
    folders: merged.folders,
    updatedSessionIds,
    preferences: mergedPrefs,
  };
}
